import React, { useState, useEffect } from 'react'
import { Modal, Select, Checkbox, Input, DatePicker, Divider, Typography, theme } from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useWorkshops, useAssignInterventionWorkshop } from '@/hooks/useWorkshops'
import { useCreateWorkshopQuotes, useWorkshopQuotes } from '@/hooks/useWorkshopQuotes'
import type { Intervention } from '@/types'

const { Text } = Typography

interface AssegnaOfficinaModalProps {
  interventionId: number
  workOrderId: number
  currentWorkshopId: number | null
  open: boolean
  onClose: () => void
  onSuccess: () => void
  interventions?: Intervention[]
}

const AssegnaOfficinaModal: React.FC<AssegnaOfficinaModalProps> = ({
  interventionId,
  workOrderId,
  currentWorkshopId,
  open,
  onClose,
  onSuccess,
  interventions = [],
}) => {
  const { token } = theme.useToken()
  const [selectedWorkshopId, setSelectedWorkshopId] = useState<number | null>(null)
  const [selectedInterventionIds, setSelectedInterventionIds] = useState<number[]>([interventionId])
  const [noteRichiesta, setNoteRichiesta] = useState('')
  const [dataScadenza, setDataScadenza] = useState<Dayjs | null>(null)

  const { data: workshopsData } = useWorkshops(true, { enabled: open })
  const workshops = workshopsData?.items ?? []
  const selectedWorkshop = workshops.find(w => w.id === selectedWorkshopId) ?? null
  const isExternal = selectedWorkshop?.esterna === true

  const assignMutation = useAssignInterventionWorkshop()
  const createQuotesMutation = useCreateWorkshopQuotes(workOrderId)
  const { data: existingQuotes = [] } = useWorkshopQuotes(workOrderId, { enabled: open && isExternal })

  const openQuoteInterventionIds = new Set(
    existingQuotes
      .filter(q => ['inviata', 'risposta_ricevuta', 'sopralluogo_richiesto'].includes(q.stato))
      .map(q => q.intervention_id),
  )

  const approvedQuoteMap = new Map<number, string>(
    existingQuotes
      .filter(q => q.stato === 'approvata')
      .map(q => [q.intervention_id, q.workshop_nome ?? '']),
  )

  // canSubmitAny: almeno un intervento non è né coperto da quote aperta né già affidato
  const canSubmitAny = interventions.some(inv => {
    const isMainInv = inv.id === interventionId
    const isCoveredInv = !isMainInv && openQuoteInterventionIds.has(inv.id!)
    return !isCoveredInv && !approvedQuoteMap.has(inv.id!)
  })

  useEffect(() => {
    if (open) {
      setSelectedWorkshopId(currentWorkshopId)
      setSelectedInterventionIds([interventionId])
      setNoteRichiesta('')
      setDataScadenza(null)
    }
  }, [open, currentWorkshopId, interventionId])

  const handleWorkshopChange = (v: number | undefined) => {
    setSelectedWorkshopId(v ?? null)
    setSelectedInterventionIds([interventionId])
    setNoteRichiesta('')
    setDataScadenza(null)
  }

  const handleOk = async () => {
    if (isExternal) {
      await createQuotesMutation.mutateAsync({
        intervention_ids: selectedInterventionIds,
        workshop_id: selectedWorkshopId!,
        note_richiesta: noteRichiesta.trim() || null,
        data_scadenza_risposta: dataScadenza ? dataScadenza.format('YYYY-MM-DD') : null,
      })
    } else {
      await assignMutation.mutateAsync({ workOrderId, interventionId, workshopId: selectedWorkshopId })
    }
    onSuccess()
    onClose()
  }

  const okDisabled = isExternal && (selectedInterventionIds.length === 0 || !canSubmitAny)
  const isPending = isExternal ? createQuotesMutation.isPending : assignMutation.isPending
  const title = isExternal ? 'Richiesta preventivo officina esterna' : 'Assegna a officina'
  const okText = isExternal ? 'Invia richiesta' : 'Conferma'

  return (
    <Modal
      open={open}
      title={title}
      okText={okText}
      cancelText="Annulla"
      confirmLoading={isPending}
      okButtonProps={{ disabled: okDisabled }}
      onOk={handleOk}
      onCancel={onClose}
      destroyOnClose
    >
      <Select
        style={{ width: '100%', marginTop: 8 }}
        placeholder="Seleziona officina..."
        value={selectedWorkshopId ?? undefined}
        onChange={handleWorkshopChange}
        allowClear
        showSearch
        optionFilterProp="label"
        options={[
          workshops.filter(w => !w.esterna).length > 0 && {
            label: 'Officine interne',
            options: workshops.filter(w => !w.esterna).map(w => ({ label: w.nome, value: w.id })),
          },
          workshops.filter(w => w.esterna).length > 0 && {
            label: 'Officine esterne — richiesta di preventivo',
            options: workshops.filter(w => w.esterna).map(w => ({ label: w.nome, value: w.id })),
          },
        ].filter(Boolean) as { label: string; options: { label: string; value: number }[] }[]}
      />
      {workshops.length === 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: token.colorWarning }}>
          Nessuna officina configurata. Aggiungine una in Anagrafica → Officine.
        </div>
      )}
      {!isExternal && selectedWorkshopId == null && currentWorkshopId != null && (
        <div style={{ marginTop: 8, fontSize: 12, color: token.colorWarning }}>
          Lasciando vuoto rimuovi l&apos;assegnazione corrente.
        </div>
      )}

      {isExternal && (
        <>
          <Divider style={{ marginTop: 12, marginBottom: 8 }} />

          <div style={{ fontSize: 12, color: token.colorTextSecondary, marginBottom: 6 }}>
            Interventi da preventivare
          </div>
          <Checkbox.Group
            value={selectedInterventionIds}
            onChange={vals => setSelectedInterventionIds(vals as number[])}
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          >
            {interventions.map(inv => {
              const isMain = inv.id === interventionId
              const isCovered = !isMain && openQuoteInterventionIds.has(inv.id!)
              const isApproved = approvedQuoteMap.has(inv.id!)
              return (
                <Checkbox key={inv.id} value={inv.id} disabled={isMain || isCovered || isApproved}>
                  {inv.descrizione_intervento}
                  {isCovered && (
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                      (richiesta già aperta)
                    </Text>
                  )}
                  {isApproved && (
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                      (già affidato a {approvedQuoteMap.get(inv.id!) || 'officina esterna'})
                    </Text>
                  )}
                </Checkbox>
              )
            })}
          </Checkbox.Group>
          {!canSubmitAny && interventions.length > 0 && (
            <div style={{
              marginTop: 6,
              fontSize: 12,
              color: token.colorWarning,
            }}>
              Tutti gli interventi sono già affidati o hanno una trattativa in corso. Non è possibile aprire una nuova richiesta.
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, color: token.colorTextSecondary, marginBottom: 4 }}>
              Nota per l&apos;officina (opzionale)
            </div>
            <Input.TextArea
              rows={2}
              placeholder="Istruzioni, riferimenti, dettagli..."
              value={noteRichiesta}
              onChange={e => setNoteRichiesta(e.target.value)}
            />
          </div>

          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, color: token.colorTextSecondary, marginBottom: 4 }}>
              Scadenza risposta (opzionale)
            </div>
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder="Nessuna scadenza"
              value={dataScadenza}
              onChange={v => setDataScadenza(v)}
              disabledDate={d => d.isBefore(dayjs(), 'day')}
            />
          </div>

          <div style={{
            marginTop: 12,
            padding: '8px 10px',
            background: token.colorFillSecondary,
            borderRadius: token.borderRadius,
            fontSize: 12,
            color: token.colorTextSecondary,
          }}>
            Richiesta a <strong>{selectedWorkshop?.nome}</strong> per{' '}
            <strong>{selectedInterventionIds.length}</strong>{' '}
            {selectedInterventionIds.length === 1 ? 'intervento' : 'interventi'}
            {noteRichiesta.trim() ? ' con nota' : ''}.
            {dataScadenza ? ` Scadenza: ${dataScadenza.format('DD/MM/YYYY')}.` : ''}
          </div>
        </>
      )}
    </Modal>
  )
}

export default AssegnaOfficinaModal
